"use client";
import { IProducts } from "@/api/AppApi/services/home/interfaces/IProduct";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import ProductBox from "../product-box/ProductBox";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { useMemo } from "react";
import { processProductsData } from "../common/functions";

interface Props {
  products: IProducts;
  setIslastProduct?: (isLastProduct: boolean) => void;
  scrollableTabs?: boolean;
  theme: ITheme;
  showCustomEventData?: boolean;
  /** Server-generated shuffle seed — keeps the random order identical across SSR and hydration. */
  shuffleSeed?: number;
  /**
   * How many leading cards to mark `priority` (eager mount + eager image).
   * Opt-in and 0 by default: most ProductGrid instances (associated products,
   * scrollable tabs, product tabs) sit BELOW the fold, where eager-loading would
   * compete with the real LCP image. Set it only where the grid is the first
   * thing on screen — e.g. the category/search results grid.
   */
  priorityCount?: number;
}
const ProductGrid: React.FC<Props> = ({
  products,
  scrollableTabs,
  theme,
  showCustomEventData,
  shuffleSeed,
  priorityCount = 0,
}) => {
  // Apply random selection if needed (when not using ProductGridWrapper)
  const processedProducts = useMemo(() => {
    return processProductsData(products, undefined, undefined, shuffleSeed);
  }, [products, shuffleSeed]);

  const productSize = processedProducts.configurations?.style_type!;
  const isSmall =
    productSize === Size.small || productSize === Size.vertical_small;
  const gridClaaName = `product-grid-${isSmall ? "small" : "large"}${
    scrollableTabs ? "-scrollable-tabs" : ""
  }`;
  return (
    <div className={gridClaaName}>
      {processedProducts?.data.map((single, key) => (
        <ProductBox
          theme={theme}
          key={key}
          size={processedProducts.configurations?.style_type!}
          {...single}
          scrollableTabs={scrollableTabs!}
          // Was `key < 0`, which is never true — so NO product image was ever
          // eager and the above-the-fold grid always waited for the observer.
          priority={key < priorityCount}
          Customize={processedProducts.configurations?.customize}
          isGrid
          showCustomEventData={showCustomEventData}
        />
      ))}
    </div>
  );
};

export default ProductGrid;
